import { ExternalLink } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toApiError } from '@/api/errors'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Spinner } from '@/components/ui/Spinner'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/cn'
import { presenceService, type ActivityVisit, type OnlineUser, type UserActivity } from '@/services/core/presence.service'
import { useStores } from '@/stores'
import { isPathAllowed } from './access'
import { formatPresenceDuration, PresenceDot, presencePageLabel, presencePageLine, presenceRoleLine } from './presence'

/** Activity dates are Asia/Tashkent days, whatever the viewer's clock. */
const ZONE = 'Asia/Tashkent'
const dayOf = (iso: string) => new Intl.DateTimeFormat('en-CA', { timeZone: ZONE }).format(new Date(iso))

/**
 * One person's admin console activity (GET /presence/users/:id/activity):
 * where they are now, the last 7 days as active/idle bars, and the pages of
 * the chosen day with their times.
 */
export const UserActivityDialog = observer(function UserActivityDialog({ user, onClose }: { user: OnlineUser; onClose: () => void }) {
  const { t, tag } = useI18n()
  const { session, ui } = useStores()
  const navigate = useNavigate()
  const [activity, setActivity] = useState<UserActivity | null>(null)
  const [error, setError] = useState('')
  const [day, setDay] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    presenceService
      .activity(user.profile_id, {}, controller.signal)
      .then((result) => {
        setActivity(result)
        setDay(result.to)
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(toApiError(cause).message)
      })
    return () => controller.abort()
  }, [user.profile_id])

  const now = activity ? activity.online : user
  const selected = activity?.days.find((item) => item.date === day)
  const longest = Math.max(1, ...(activity?.days.map((item) => item.online_seconds) ?? [0]))
  const visits = useMemo(
    () => (activity?.visits ?? []).filter((visit) => dayOf(visit.started_at) === day).reverse(),
    [activity, day],
  )
  const time = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit', timeZone: ZONE }) : '—'
  const dateLabel = (date: string, options: Intl.DateTimeFormatOptions) =>
    new Date(`${date}T12:00:00+05:00`).toLocaleDateString(tag, { ...options, timeZone: ZONE })
  const openPage = (path: string) => {
    onClose()
    navigate(path)
  }

  return (
    <Dialog open onClose={onClose} size="lg" title={t('layout.presence.activityTitle')}>
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <span className="relative shrink-0">
            <Avatar name={user.display_name} src={user.avatar_url} size={44} />
            <PresenceDot status={now ? now.status : 'offline'} className="size-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[length:calc(15px*var(--text-scale))] font-semibold text-fg">{user.display_name}</div>
            {presenceRoleLine(user, ui.contentLocale) && (
              <div className="truncate text-[length:calc(12.5px*var(--text-scale))] text-muted">{presenceRoleLine(user, ui.contentLocale)}</div>
            )}
            <div className={cn('truncate text-[length:calc(12.5px*var(--text-scale))]', !now ? 'text-subtle' : now.status === 'idle' ? 'text-warning' : 'text-success')}>
              {now ? `${t(now.status === 'idle' ? 'layout.presence.idle' : 'layout.presence.active')} · ${presencePageLine(now, t)}` : t('layout.presence.offline')}
            </div>
          </div>
          {now && isPathAllowed(now.path, session) && (
            <Button variant="secondary" size="sm" icon={<ExternalLink className="size-3.5" />} onClick={() => openPage(now.path)}>
              {t('layout.presence.openPage')}
            </Button>
          )}
        </div>

        {error && <p className="text-[length:calc(12.5px*var(--text-scale))] text-danger">{t('layout.presence.activityFailed')}: {error}</p>}
        {!activity && !error && (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        )}

        {activity && (
          <>
            <section className="flex flex-col gap-1.5">
              <h3 className="text-[length:calc(12px*var(--text-scale))] font-medium uppercase tracking-wide text-muted">{t('layout.presence.week')}</h3>
              {[...activity.days].reverse().map((item) => (
                <button
                  key={item.date}
                  type="button"
                  onClick={() => setDay(item.date)}
                  className={cn(
                    'grid grid-cols-[88px_1fr_96px] items-center gap-3 rounded-[calc(8px*var(--radius-scale))] px-2 py-1 text-left transition-colors hover:bg-hover',
                    item.date === day && 'bg-hover',
                  )}
                >
                  <span className="num text-[length:calc(12.5px*var(--text-scale))] text-fg-2">{dateLabel(item.date, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                  <span className="flex h-2.5 overflow-hidden rounded-full bg-line">
                    <span className="bg-success" style={{ width: `${(item.active_seconds / longest) * 100}%` }} />
                    <span className="bg-warning" style={{ width: `${(item.idle_seconds / longest) * 100}%` }} />
                  </span>
                  <span className="num text-right text-[length:calc(12px*var(--text-scale))] text-muted">
                    {item.online_seconds > 0 ? formatPresenceDuration(item.online_seconds, t) : '—'}
                  </span>
                </button>
              ))}
            </section>

            {selected && (
              <section className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {[
                  [t('layout.presence.online'), formatPresenceDuration(selected.online_seconds, t)],
                  [t('layout.presence.active'), formatPresenceDuration(selected.active_seconds, t)],
                  [t('layout.presence.idle'), formatPresenceDuration(selected.idle_seconds, t)],
                  [t('layout.presence.firstSeen'), time(selected.first_seen_at)],
                  [t('layout.presence.lastSeen'), time(selected.last_seen_at)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-[calc(10px*var(--radius-scale))] border border-line px-3 py-2">
                    <div className="text-[length:calc(11px*var(--text-scale))] text-muted">{label}</div>
                    <div className="num text-[length:calc(14px*var(--text-scale))] font-semibold text-fg">{value}</div>
                  </div>
                ))}
              </section>
            )}

            <section className="flex flex-col gap-1">
              <h3 className="text-[length:calc(12px*var(--text-scale))] font-medium uppercase tracking-wide text-muted">
                {t('layout.presence.timeline', { date: dateLabel(day, { day: 'numeric', month: 'long' }) })}
              </h3>
              {visits.length === 0 && <p className="py-4 text-center text-[length:calc(13px*var(--text-scale))] text-muted">{t('layout.presence.noVisits')}</p>}
              <ol className="max-h-[min(320px,40vh)] overflow-y-auto">
                {visits.map((visit) => (
                  <VisitRow key={visit.id} visit={visit} time={time} onOpen={isPathAllowed(visit.path, session) ? () => openPage(visit.path) : undefined} />
                ))}
              </ol>
            </section>
          </>
        )}
      </div>
    </Dialog>
  )
})

function VisitRow({ visit, time, onOpen }: { visit: ActivityVisit; time: (iso: string | null) => string; onOpen?: () => void }) {
  const { t } = useI18n()
  const label = presencePageLabel(visit.path, t)
  const end = visit.ended_at ?? visit.last_seen_at
  const span = (new Date(end).getTime() - new Date(visit.started_at).getTime()) / 1000

  return (
    <li className="grid grid-cols-[96px_1fr_auto] items-baseline gap-3 border-b border-line px-2 py-1.5 last:border-0">
      <span className="num text-[length:calc(12px*var(--text-scale))] text-muted">
        {time(visit.started_at)}–{visit.ended_at ? time(visit.ended_at) : t('layout.presence.openVisit')}
      </span>
      <span className="min-w-0">
        {onOpen ? (
          <button type="button" onClick={onOpen} className="block max-w-full truncate text-left text-[length:calc(13px*var(--text-scale))] text-fg hover:underline">
            {label}
          </button>
        ) : (
          <span className="block truncate text-[length:calc(13px*var(--text-scale))] text-fg">{label}</span>
        )}
        <span className="block text-[length:calc(11.5px*var(--text-scale))] text-subtle">
          {t('layout.presence.activeShort', { duration: formatPresenceDuration(visit.active_seconds, t) })}
          {visit.idle_seconds > 0 && <> · <span className="text-warning">{t('layout.presence.idleShort', { duration: formatPresenceDuration(visit.idle_seconds, t) })}</span></>}
        </span>
      </span>
      <span className="num text-[length:calc(12px*var(--text-scale))] text-fg-2">{formatPresenceDuration(span, t)}</span>
    </li>
  )
}
